"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { Image } from "lucide-react";
import config from "../../config/config";
import SectionHeader from "./SectionHeader";
import PhotoCard from "../cards/PhotoCard";
import PostsSkeleton from "../posts/PostsSkeleton";

export default function PhotoSection() {
  const [photos, setPhotos] = useState([]);
  const [photoSettings, setPhotoSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch homepage settings for photo section
        const settingsResponse = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/photo`);
        const settingsData = await settingsResponse.json();

        if (settingsData.success && settingsData.data) {
          setPhotoSettings(settingsData.data);

          // Only fetch photos if section is enabled
          if (settingsData.data.enabled !== false) {
            const photosResponse = await fetch(
              `${config.NEXT_PUBLIC_API_URL}/api/public/p/gallery?limit=6`
            );

            if (photosResponse.ok) {
              const photosData = await photosResponse.json();
              if (photosData.success) {
                setPhotos(photosData.data.posts ? photosData.data.posts : []);
              }
            }
          }
        }
      } catch (error) {

      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  const getLabel = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] ? field[language] : (field.en ? field.en : '');
    }
    return '';
  };

  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto w-full">
          <SectionHeader
            icon={Image}
            sectionTitle="Gallery"
            mainHeading="Latest Photos"
          />
          <PostsSkeleton />
        </div>
      </section>
    );
  }

  // Hide section if disabled or no meaningful content
  if (photoSettings.enabled === false) {
    return null;
  }
  if (!getLabel(photoSettings.sectionTitle) && !getLabel(photoSettings.sectionSubtitle) && photos.length === 0) {
    return null;
  }

  return (
    <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6" style={{ background: 'linear-gradient(to bottom right, var(--color-background), var(--color-muted), var(--color-background))' }}>
      <div className="max-w-7xl mx-auto w-full">
        <SectionHeader
          icon={Image}
          sectionTitle={photoSettings.sectionTitle}
          mainHeading={photoSettings.sectionSubtitle}
          description={photoSettings.description}
          buttonText={photoSettings.viewAllButtonText}
          buttonLink={photos.length > 0 && photoSettings.showViewAllButton !== false ? (photoSettings.viewAllButtonLink || '/gallery') : null}
          buttonTarget={photoSettings.viewAllButtonTarget || '_self'}
          getLabel={getLabel}
        />

        {/* Photos Grid - Instagram Style */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-2">
          {photos.map((photo, index) => (
            <PhotoCard key={photo._id || photo.id} photo={photo} />
          ))}
        </div>


      </div>
    </section>
  );
}